// ./src/components/generic/CollapsibleSection/CollapsibleSection.tsx
//
// Reusable collapsible section component that combines a header with
// expand/collapse behavior. Replaces the duplicated SectionHeader + Collapse
// pattern found across edit drawers (Refuel, Expense, Checkpoint, Glovebox).
//
// Supports:
// - Collapsible mode (default): clickable header toggles content visibility
// - Always-expanded mode: header is non-interactive, content always visible
// - Optional badge count (e.g., tag count, file count)
// - Optional "(optional)" label
// - Optional leading divider
// - Controlled or uncontrolled expand state

import React, { useState, useCallback } from "react";
import {
  Stack,
  Group,
  Text,
  Collapse,
  UnstyledButton,
  Divider,
  MantineSize,
} from "@mantine/core";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface CollapsibleSectionProps {
  /** Section title */
  title: string;

  /** Whether the section content is visible (controlled mode) */
  expanded?: boolean;

  /** Callback when expand/collapse state changes (controlled mode) */
  onToggle?: () => void;

  /** Initial expanded state (uncontrolled mode — ignored if expanded + onToggle are provided) */
  defaultExpanded?: boolean;

  /** Whether to show "(optional)" label next to the title */
  optional?: boolean;

  /** Badge count shown next to the title (e.g., number of tags, files) */
  badge?: number;

  /** If true, the section is always expanded and the header is not clickable */
  alwaysExpanded?: boolean;

  /** Whether to show a Divider above the section header */
  withDivider?: boolean;

  /** Divider label (if withDivider is true, optionally show text in the divider instead of a plain line) */
  dividerLabel?: string;

  /** Gap between children inside the collapsed content */
  gap?: MantineSize | number;

  /** Custom icon to show in the header (replaces chevron on the left) */
  iconName?: string;

  /** Section content */
  children: React.ReactNode;

  /** Whether to disable the section (grayed out, not toggleable) */
  disabled?: boolean;
}

// ============================================
// Component
// ============================================

const CollapsibleSectionComponent: React.FC<CollapsibleSectionProps> = ({
  title,
  expanded: controlledExpanded,
  onToggle: controlledOnToggle,
  defaultExpanded = false,
  optional = false,
  badge,
  alwaysExpanded = false,
  withDivider = false,
  dividerLabel,
  gap = "sm",
  iconName,
  children,
  disabled = false,
}) => {
  // Uncontrolled state (used when expanded/onToggle are not provided)
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Determine if we're in controlled mode
  const isControlled =
    controlledExpanded !== undefined && controlledOnToggle !== undefined;

  const isExpanded = alwaysExpanded
    ? true
    : isControlled
      ? controlledExpanded
      : internalExpanded;

  const handleToggle = useCallback(() => {
    if (alwaysExpanded || disabled) return;

    if (isControlled) {
      controlledOnToggle!();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  }, [alwaysExpanded, disabled, isControlled, controlledOnToggle]);

  // ── Header content ──

  const headerContent = (
    <Group justify="space-between" py="xs">
      <Group gap="xs">
        {iconName && (
          <AppIcon
            iconName={iconName}
            size={16}
            color={disabled ? "var(--mantine-color-gray-4)" : undefined}
          />
        )}
        <Text fw={600} size="sm" c={disabled ? "dimmed" : undefined}>
          {title}
        </Text>
        {optional && (
          <Text size="xs" c="dimmed">
            (optional)
          </Text>
        )}
        {badge !== undefined && badge > 0 && (
          <Text size="xs" c="blue" fw={500}>
            ({badge})
          </Text>
        )}
      </Group>
      {!alwaysExpanded && (
        <AppIcon
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          size={16}
          color={disabled ? "var(--mantine-color-gray-4)" : undefined}
        />
      )}
    </Group>
  );

  return (
    <>
      {/* Optional divider above section */}
      {withDivider && (
        dividerLabel ? (
          <Divider label={dividerLabel} labelPosition="left" />
        ) : (
          <Divider />
        )
      )}

      {/* Header: clickable button or static text */}
      {alwaysExpanded ? (
        headerContent
      ) : (
        <UnstyledButton
          onClick={handleToggle}
          w="100%"
          disabled={disabled}
          style={disabled ? { cursor: "not-allowed", opacity: 0.6 } : undefined}
        >
          {headerContent}
        </UnstyledButton>
      )}

      {/* Content */}
      {alwaysExpanded ? (
        <Stack gap={gap}>{children}</Stack>
      ) : (
        <Collapse in={isExpanded}>
          <Stack gap={gap}>{children}</Stack>
        </Collapse>
      )}
    </>
  );
};

export const CollapsibleSection = React.memo(CollapsibleSectionComponent);